import React, { useEffect } from 'react'
import ProgramEditingSubSection from './Components/ProgramEditingSubSection'
import ProgramOption from './Components/ProgramOption'
import ProgramPriceCoverage from 'core-system/Program/ProgramPriceCoverage'
import FlexUtils from 'features/Flex/Shared/FlexUtils'
import FormattingUtils from 'shared/FormattingUtils'
import { getAllEmployeeSegment } from '../Shared/helperFunctions'
import BudgetUsesSelector from 'features/Programs/Editing/Components/BudgetUsesSelector'
import { Segment } from 'redux/employeePlatform/employeePlatformTypes'
import {
  flexType,
  flexActiveOptionsType,
  availableFlexMerchant,
  BudgetCycle,
} from '../Shared/types'
import { FLEX_INITIAL_STATE } from './ProgramEditingSlice'
import Radio from 'core-system/Radio'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import SwitchToggle from 'core-system/SwitchToggle'
import ProgramSectionSubHeading from './Components/ProgramSectionSubHeading'
import ProrationToggle from '../Shared/ProrationToggle'

interface FlexProgramEditingProps {
  flex: flexType
  availableMerchants: availableFlexMerchant
  getFlexRecommendations: (segmentId: string) => void
  changeFlexActiveMerchantIds: (
    type: flexActiveOptionsType,
    value: string[]
  ) => void
  changeFlexActiveOptions: (type: flexActiveOptionsType, value: boolean) => void
  changeFlexBudget: (budget: number) => void
  changeFlexBudgetCycle: (cycle: BudgetCycle) => void
  changeFlexOneTimePurchase: (value: boolean) => void
  changeFlexRolloverBudget: (value: boolean) => void
  changeFlexProrated: (value: boolean) => void
  isContinued: boolean
  allSegments: Segment[]
}

const FlexProgramEditing = ({
  flex = FLEX_INITIAL_STATE,
  availableMerchants,
  getFlexRecommendations,
  changeFlexActiveMerchantIds,
  changeFlexActiveOptions,
  changeFlexBudget,
  changeFlexBudgetCycle,
  changeFlexOneTimePurchase,
  changeFlexRolloverBudget,
  changeFlexProrated,
  isContinued,
  allSegments,
}: FlexProgramEditingProps) => {
  const flexMerchantIds = availableMerchants?.flex.map((m) => m.id) || []

  useEffect(() => {
    // If the program is continued, we don't need to add active merchant ids
    if (availableMerchants && !isContinued) {
      changeFlexActiveMerchantIds('flex', flexMerchantIds)
    }
  }, [availableMerchants])

  useEffect(() => {
    if (allSegments.length > 0) {
      const allEmployeeSegment = getAllEmployeeSegment(allSegments)
      getFlexRecommendations(allEmployeeSegment.id)
    }
  }, [allSegments])

  const monthlyGas = Math.min(
    Math.round(((flex.budget ? flex.budget : 0) / FlexUtils.monthlyGas) * 100),
    100
  )

  const handleTypeToggle = (type: flexActiveOptionsType) => {
    const newActive = !flex.activeOptions[type]
    changeFlexActiveOptions(type, newActive)
  }

  const handleProviderChange = (
    type: flexActiveOptionsType,
    newActiveMerchantIds: string[]
  ) => {
    changeFlexActiveMerchantIds(type, newActiveMerchantIds)
  }

  const budgetCycle = flex.budgetCycle || 'MONTHLY'
  const budgetSubtitleByCycle: Record<BudgetCycle, string> = {
    DAILY: 'Daily Budget Per Commuter',
    MONTHLY: 'Monthly Budget Per Commuter',
    QUARTERLY: 'Quarterly Budget Per Commuter',
    YEARLY: 'Yearly Budget Per Commuter',
  }

  return (
    <>
      <ProgramEditingSubSection>
        <BudgetUsesSelector
          budget={flex.budget}
          onBudgetChange={(budget) => changeFlexBudget(budget * 100)}
          budgetSubtitle={budgetSubtitleByCycle[budgetCycle]}
          showUses={false}
          budgetError={flex.budget <= 0}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <FlexContainer
          alignItems='center'
          justifyContent='space-between'
          marginBottom='1rem'
          style={{ gap: '1rem' }}
        >
          <FlexContainer flexDirection='column' style={{ gap: '0.25rem' }}>
            <Text variant='h5'>One-time purchase</Text>
            <Text variant='body1' color='grey600'>
              Commuter can only do one transaction per cycle
            </Text>
          </FlexContainer>
          <SwitchToggle
            active={flex.oneTimePurchase ?? false}
            onClick={() =>
              changeFlexOneTimePurchase(!(flex.oneTimePurchase ?? false))
            }
          />
        </FlexContainer>
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <FlexContainer
          alignItems='center'
          justifyContent='space-between'
          marginBottom='1rem'
          style={{ gap: '1rem' }}
        >
          <FlexContainer flexDirection='column' style={{ gap: '0.25rem' }}>
            <Text variant='h5'>Rollover unused budget</Text>
            <Text variant='body1' color='grey600'>
              Unspent budget at the end of each cycle is added to the next cycle
            </Text>
          </FlexContainer>
          <SwitchToggle
            active={flex.rolloverBudget ?? false}
            onClick={() =>
              changeFlexRolloverBudget(!(flex.rolloverBudget ?? false))
            }
          />
        </FlexContainer>
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProrationToggle
          active={flex.prorated ?? false}
          onClick={() => changeFlexProrated(!(flex.prorated ?? false))}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramSectionSubHeading title='How often should the limit reset?' />
        <FlexContainer flexDirection='column' style={{ gap: '1rem' }}>
          <FlexContainer alignItems='center'>
            <Radio
              active={budgetCycle === 'DAILY'}
              onClick={() => changeFlexBudgetCycle('DAILY')}
            />
            <Text variant='body1' marginLeft='0.5rem'>
              Daily
            </Text>
          </FlexContainer>
          <FlexContainer alignItems='center'>
            <Radio
              active={budgetCycle === 'MONTHLY'}
              onClick={() => changeFlexBudgetCycle('MONTHLY')}
            />
            <Text variant='body1' marginLeft='0.5rem'>
              Monthly
            </Text>
          </FlexContainer>
          <FlexContainer alignItems='center'>
            <Radio
              active={budgetCycle === 'QUARTERLY'}
              onClick={() => changeFlexBudgetCycle('QUARTERLY')}
            />
            <Text variant='body1' marginLeft='0.5rem'>
              Quarterly
            </Text>
          </FlexContainer>
          <FlexContainer alignItems='center'>
            <Radio
              active={budgetCycle === 'YEARLY'}
              onClick={() => changeFlexBudgetCycle('YEARLY')}
            />
            <Text variant='body1' marginLeft='0.5rem'>
              Yearly
            </Text>
          </FlexContainer>
        </FlexContainer>
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramOption
          type='flex'
          active={flex.activeOptions.flex}
          activeMerchantIds={flex.activeMerchantIds.flex}
          availableMerchantIds={flexMerchantIds}
          handleProviderChange={(newActiveMerchantIds) =>
            handleProviderChange('flex', newActiveMerchantIds)
          }
          handleToggle={() => handleTypeToggle('flex')}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection isLastSection>
        <ProgramOption
          type='gas'
          active={flex.activeOptions.gas}
          handleToggle={() => handleTypeToggle('gas')}
        >
          <ProgramPriceCoverage
            percentage={monthlyGas}
            title='Monthly Gas Price Covered'
            description={`Your budget will cover ${monthlyGas}% of the average monthly
                  gas spend (${FormattingUtils.formatDollar(
                    FlexUtils.monthlyGas
                  )}) for your commuters.`}
          />
        </ProgramOption>
        <br />
        <ProgramOption
          type='transit'
          active={flex.activeOptions.transit}
          handleToggle={() => handleTypeToggle('transit')}
        ></ProgramOption>
        <br />
        <ProgramOption
          type='parking'
          active={flex.activeOptions.parking}
          handleToggle={() => handleTypeToggle('parking')}
        ></ProgramOption>
        <br />
        <ProgramOption
          type='tolls'
          active={flex.activeOptions.tolls}
          handleToggle={() => handleTypeToggle('tolls')}
        ></ProgramOption>
        <br />
        <ProgramOption
          type='rideshare'
          active={flex.activeOptions.rideshare}
          handleToggle={() => handleTypeToggle('rideshare')}
        ></ProgramOption>
        <br />
        <ProgramOption
          type='carshare'
          active={flex.activeOptions.carshare}
          handleToggle={() => handleTypeToggle('carshare')}
        ></ProgramOption>
      </ProgramEditingSubSection>
    </>
  )
}

export default React.memo(FlexProgramEditing)
